import type { VisitNoSignatureReason, VisitOutcome } from './serviceReport';
import type { WorkOrderVisitPerson, WorkOrderVisitStatus } from './visits';
import type { ReportRecipientsFrom } from './jobMessages';
import type { ServiceReportBar, ServiceReportFactsAnswer, VisitPhotoAsset } from './serviceReportDocument';
import type { DocumentFileRow } from './salesOrder';

/**
 * 🔒 The visit's page (piece 29; decisions 1902, 1908, 1910, 2062; T1263): the
 * trip's four stamps with who made each, who went with the lead marked, the
 * lines worked with the work performed, the parts fitted and the completion
 * sheet, with Complete the Visit for the lead, Service Report: Print and Send,
 * and Book a Return Visit. These are its shapes and the rules that need no
 * database.
 */

/** 🔒 The trip's four stamps, in the order a visit makes them (decision 1902). */
export const VISIT_STAMPS = ['Dispatched', 'EnRoute', 'Started', 'Completed'] as const;
export type VisitStampKind = (typeof VISIT_STAMPS)[number];

export const VISIT_STAMP_LABELS: Readonly<Record<VisitStampKind, string>> = {
  Dispatched: 'Dispatched',
  EnRoute: 'En Route',
  Started: 'Started',
  Completed: 'Completed',
};

/** One stamp: when it was made and by whom, each null until it is. */
export interface VisitStamp {
  kind: VisitStampKind;
  at: string | null;
  by: string | null;
}

/** One line the visit worked, with the account typed beneath it and the visit's hours on it. */
export interface VisitPageLine {
  lineId: string;
  /** The line's place on the job, 1 and on. */
  lineNumber: number;
  description: string;
  workPerformed: string | null;
  /** The visit's own hours on the line, or null where no entry on the visit names it. */
  hours: number | null;
  /** 🔒 Decision 4121: the notes posted under this line, by id in the feed's order; absent from an older reply. */
  noteIds?: string[];
  /** 🔒 Decision 5571: the workers the visit sent for this line, by name; empty where it sends nobody, absent from an older reply. */
  sentNames?: string[];
}

/** 🔒 T3851: a line of the completion sheet still Pending, as the sheet names it. */
export interface VisitPendingLine {
  lineId: string;
  lineNumber: number;
  description: string;
}

/** One part fitted on the job (decision 2061: a fitted part names no visit). */
export interface VisitPagePart {
  id: string;
  item: string;
  quantity: number;
  unit: string;
}

/** A known contact at the customer that Service Report: Send offers (decision 2062). */
export interface VisitReportRecipient {
  personId: string;
  name: string;
  /** The contact's primary work email; a contact with none is drawn and cannot be ticked. */
  address: string | null;
  ticked: boolean;
}

/**
 * 🔒 One row of the report's History tab (decision 3960): what was done to the
 * visit, read off its own audit rows, newest first, with who did it.
 */
export interface VisitHistoryRow {
  id: string;
  label: string;
  /** Who made it, and where a send went; null where neither is known. */
  detail: string | null;
  at: string;
}

/** What the visit's page reads, in one answer. */
export interface VisitPageAnswer {
  workOrderId: string;
  workOrderNumber: string;
  companyId: string;
  companyName: string;
  siteName: string;
  visit: {
    id: string;
    version: number;
    label: string;
    status: WorkOrderVisitStatus;
    windowStart: string;
    windowEnd: string | null;
    outcome: VisitOutcome | null;
    outcomeNote: string | null;
    workSummary: string | null;
    customerComment: string | null;
    noSignatureReason: VisitNoSignatureReason | null;
    cancelReason: string | null;
    /** The customer's typed name on the signed sheet, or null where a reason stands in its place. */
    customerSigner: string | null;
    leadSigner: string | null;
    /** The frozen service report, once the lead has signed. */
    serviceReportFileId: string | null;
    /** 🔒 The version Complete the Visit wrote, which Print opens; the frozen file takes no other. */
    serviceReportVersionId: string | null;
    /**
     * 🔒 Decision 13090: the path the signed sheet opens on for this viewer, relative to the API root, decided once on
     * the server: the visit's sheet route for a worker its assignment names, the file route at the signed version for
     * anyone else. Null while the report is open; absent from an older reply, which reads the file route.
     */
    serviceReportPath?: string | null;
    /** 🔒 Whether the visit carries the job's own booking, whose move re-reads the tier and records why (decision 2813). */
    carriesBooking?: boolean;
    /** 🔒 Decision 3100: why the visit cannot move, which the page names as Fixed; null or absent reads Can move. */
    fixedReason?: string | null;
  };
  stamps: VisitStamp[];
  /** Who went, the lead first. */
  crew: WorkOrderVisitPerson[];
  /** The lines the completion sheet and the printed report list: those a time entry on this visit names, else every line of the job. */
  lines: VisitPageLine[];
  /**
   * 🔒 Decision 4320: Lines Worked's cards. While the report is Open, every line of the job in the job's order, each with this
   * visit's hours or null; a signed report's cards are `lines`. Absent from an older reply, which reads `lines`.
   */
  cards?: VisitPageLine[];
  /**
   * 🔒 T3851: the lines of the completion sheet still Pending, in the job's order. A visit completed in full waits on them, and the
   * sheet names them beside the unanswered checklist items; absent from an older reply, which reads as none.
   */
  pendingLines?: VisitPendingLine[];
  parts: VisitPagePart[];
  /** Whether the viewer is the visit's lead. */
  viewerIsLead: boolean;
  /** 🔒 Complete the Visit stands for the lead alone, on a visit already started (decision 2061). */
  canComplete: boolean;
  /** Book a Return Visit stands where the job takes another visit and the viewer writes it. */
  canBookReturn: boolean;
  /** 🔒 Edit Visit stands on a visit still open where the viewer writes the job (decision 2813). */
  canEdit?: boolean;
  /** Service Report: Send stands once the sheet is frozen and the viewer writes the job. */
  canSend: boolean;
  /** 🔒 T3833: whether the viewer writes the job, which offers Share and the pencil on the Files tab's rows; absent from an older reply. */
  writesJob?: boolean;
  /**
   * 🔒 T3833: the Files tab's rows, the sheet's first and then each note photo's in the notes' order, each as the documents'
   * attachments read answers it, read under the visit's own read so a booked worker sees them (decision 5572). A deleted
   * file has no row. Absent from an older reply.
   */
  files?: VisitFileRow[];
  /**
   * 🔒 Decision 13310: whether the viewer may link a photo to an asset, the note's writer rule (a worker on the visit or a
   * viewer holding the Work Order write); absent from an older reply, which reads as no.
   */
  canLinkAsset?: boolean;
  /**
   * 🔒 Decision 13310: the assets Link to Asset offers, those the job's lines name in the job's order and then the nodes
   * standing at the job's site; empty where the viewer may not link or the visit holds no photo.
   */
  assetChoices?: VisitPhotoAsset[];
  /** Whom Send offers, with the ticks it opens on; empty until the sheet is frozen. */
  recipients: VisitReportRecipient[];
  /** 🔒 Decision 2080: whether the ticks are the Complete rule's recipients, or the interim's where no rule is in force (decision 2062). */
  recipientsFrom: ReportRecipientsFrom;
  /** 🔒 Decision 2910: the visit's Service Report, its number, state and notes; absent from an older reply. */
  report?: ServiceReportFactsAnswer;
  /** 🔒 Decision 2910: the technician's bar for the viewer's own employment; null where the viewer holds none. Absent from an older reply. */
  bar?: ServiceReportBar | null;
  /** The job's state, which the bar's Clock In and Parts Fitted read; absent from an older reply. */
  workOrderStatus?: string;
  /** 🔒 Decision 3960: the History tab's rows, newest first; absent from an older reply. */
  history?: VisitHistoryRow[];
}

/** What Service Report: Send carries (decision 2062): the people ticked, and an optional line of the sender's own. */
export interface SendServiceReportInput {
  personIds: readonly string[];
  message?: string | null;
}

/** What Send answers: the addresses it went to, and whether the transport took it. */
export interface SendServiceReportAnswer {
  to: string;
  sent: 'Sent' | 'Failed';
}

/**
 * 🔒 Decision 13090: the visit's sheet route, which streams its signed sheet to a worker its assignment names, relative
 * to the API root; `GET /work-orders/:id/visits/:visitId/sheet/stream`.
 */
export function visitSheetPath(workOrderId: string, visitId: string): string {
  return `/work-orders/${encodeURIComponent(workOrderId)}/visits/${encodeURIComponent(visitId)}/sheet/stream`;
}

/** 🔒 Decision 13090: the file route at the signed version, which every viewer the assignment does not name opens. */
export function signedSheetFilePath(fileId: string, versionId: string | null): string {
  return `/files/${encodeURIComponent(fileId)}${versionId ? `/versions/${encodeURIComponent(versionId)}` : ''}/stream`;
}

/**
 * 🔒 Decision 13090: the path a client opens the signed sheet on, the answer's own `serviceReportPath`; an older reply
 * naming none reads the file route at the signed version. Null while no sheet is frozen.
 */
export function serviceReportPathOf(visit: Pick<VisitPageAnswer['visit'], 'serviceReportFileId' | 'serviceReportVersionId' | 'serviceReportPath'>): string | null {
  if (!visit.serviceReportFileId) return null;
  return visit.serviceReportPath ?? signedSheetFilePath(visit.serviceReportFileId, visit.serviceReportVersionId);
}

/**
 * 🔒 Decision 13210: a Files tab row of the visit's answer, the documents' row with the path it opens on, decided once on
 * the server: the sheet's `serviceReportPath`, and for a note photo the visit's picture route for a worker its
 * assignment names and the file route for anyone else. Absent from an older reply, which reads the file route.
 */
export interface VisitFileRow extends DocumentFileRow {
  path?: string | null;
}

/**
 * 🔒 Decision 13210: the visit's picture route, which streams a picture the visit's notes carry to a worker its
 * assignment names, relative to the API root; `GET /work-orders/:id/visits/:visitId/pictures/:fileId/stream`.
 */
export function visitPicturePath(workOrderId: string, visitId: string, fileId: string): string {
  return `/work-orders/${encodeURIComponent(workOrderId)}/visits/${encodeURIComponent(visitId)}/pictures/${encodeURIComponent(fileId)}/stream`;
}

/**
 * 🔒 Decision 13310: Link to Asset's route, which links a picture the visit's notes carry to one asset node, replacing the
 * link it held; `PUT /work-orders/:id/visits/:visitId/pictures/:fileId/asset`.
 */
export function visitPictureAssetPath(workOrderId: string, visitId: string, fileId: string): string {
  return `/work-orders/${encodeURIComponent(workOrderId)}/visits/${encodeURIComponent(visitId)}/pictures/${encodeURIComponent(fileId)}/asset`;
}

/** 🔒 Decision 13210: the file route at a picture's current version, which every viewer the assignment does not name opens. */
export function pictureFilePath(fileId: string): string {
  return `/files/${encodeURIComponent(fileId)}/stream`;
}

/** 🔒 Decision 13210: the path a client opens a visit's picture on, the answer's own; an older reply naming none reads the file route. */
export function picturePathOf(fileId: string, path: string | null | undefined): string {
  return path ?? pictureFilePath(fileId);
}

/** 🔒 Decisions 6390, 13210: a picture's thumbnail, its path with the stream's one size word, under the same gates. */
export function pictureThumbPathOf(fileId: string, path: string | null | undefined): string {
  return `${picturePathOf(fileId, path)}?size=thumb`;
}

/** 🔒 Send refuses a visit with no frozen sheet and a send ticking nobody with an address (decision 2062). */
export function sendServiceReportProblem(facts: { serviceReportFileId: string | null; addresses: readonly string[] }): string | null {
  if (!facts.serviceReportFileId) return 'the visit has no service report until its lead completes it';
  if (facts.addresses.length === 0) return 'tick at least one contact with an email address';
  return null;
}
